/**
 * @fileoverview Contexto de React para las cards contenedores de contenido del Panel Dinámico.
 *
 * Este módulo proporciona un contexto simple y un hook para compartir información
 * de contenido entre componentes dentro del árbol del Panel Dinámico.
 *
 * @module ContentContext
 * @requires React
 * 
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */

import React from 'react';

/**
 * Contexto de React para compartir contenido.
 *
 * Se utiliza para pasar datos específicos de contenido a través del árbol de componentes
 * sin necesidad de pasar props manualmente en cada card del dashboard.
 *
 * @constant {React.Context<*|null>} ContentContext
 */
const ContentContext = React.createContext(null);

/**
 * Hook personalizado para acceder al ContentContext.
 *
 * Facilita el acceso al valor del contexto y asegura que se esté utilizando
 * dentro de un proveedor válido lanzando un error si no es así.
 *
 * @function useContentContext
 * @returns {*} El valor actual del contexto de contenido.
 * @throws {Error} Si se usa fuera de un ContentContext.Provider.
 *
 * @example
 * // En un componente hijo dentro del Provider
 * const { slot } = useContentContext();
 */
const useContentContext = () => {
  const context = React.useContext(ContentContext);
  if (!context) {
    throw new Error(
      'useContentContext must be used within a ContentContext.Provider'
    );
  }
  return context;
};

export { ContentContext, useContentContext };
